import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  TextField,
  Stack
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { addMember, leaveChat, listMembers, removeMember } from "../../resources/api.resource.ts";

interface ListMembersDialogProps {
  open: boolean;
  groupId: string;
  currentUserId: string;
  onClose: () => void;
  onRemoved?: (userId: string) => void;
  onLeave?: () => void;
  onPinclose: ()=> void;
}

interface Member {
  id: string;
  name: string;
  avatar?: string;
}

const ListMembersDialog: React.FC<ListMembersDialogProps> = ({
  open,
  groupId,
  currentUserId,
  onClose,
  onRemoved,
  onLeave,
  onPinclose,
}) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [newMemberId, setNewMemberId] = useState<string>("");
  const [adding, setAdding] = useState<boolean>(false);
  const titleId = React.useId();

  useEffect(() => {
    if (!open || !groupId) return;

    const fetchMembers = async () => {
      setLoading(true);
      try {
        const res = await listMembers(groupId);
        if (!res) {
          console.error("Failed to fetch members", await res.text());
          setMembers([]);
          setLoading(false);
          return;
        }
        const memberList: Member[] = res.members ?? res.users ?? [];
        setMembers(memberList);
      } catch (err) {
        console.error("Fetch members error:", err);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [open, groupId]);


  const handleRemoveMember = async (userId: string) => {
    try {
      await removeMember( groupId, userId );
      setMembers(prev => prev.filter(m => m.id !== userId));
      if (onRemoved) onRemoved(userId);
    } catch (err) {
      console.error("Remove member error:", err);
    }
  };

  const handleLeaveRoom = async () => {
    try {
      await leaveChat({ collaboration_id: groupId });
      if (onLeave) onLeave();
      onClose();
      onPinclose();
    } catch (err) {
      console.error("Leave Group error:", err);
    }
  };

  const handleAddMember = async () => {
  if (!newMemberId.trim()) return;
  setAdding(true);
  try {
    const data = await addMember( groupId, { userId: newMemberId });
    const newMember: Member = {
      id: newMemberId,
      name: data?.members || newMemberId,
      avatar: data?.avatar || undefined,
    };

    setMembers(prev => [...prev, newMember]);
    setNewMemberId("");
  } catch (err) {
    console.error("Add member error:", err);
  } finally {
    setAdding(false);
  }
};


  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby={titleId}>
      <DialogTitle id={titleId}>
        Members
        <IconButton
          aria-label="Close members dialog"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack direction="row" spacing={1} mb={2}>
          <TextField
            label="Add Member"
            size="small"
            fullWidth
            value={newMemberId}
            onChange={(e) => setNewMemberId(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAddMember(); }}
          />
          <Button
            variant="contained"
            onClick={handleAddMember}
            disabled={adding || !newMemberId.trim()}
          >
            Add
          </Button>
        </Stack>

        {loading ? (
          <div style={{ textAlign: "center", padding: "1rem" }}>Loading...</div>
        ) : members.length === 0 ? (
          <div style={{ textAlign: "center", padding: "1rem" }}>No members found</div>
        ) : (
         <List>
  {members.map(member => (
    <ListItem
      key={member.id}
      secondaryAction={
        member.id !== currentUserId ? (
          <IconButton
            edge="end"
            color="error"
            aria-label={`Remove ${member.name}`}
            onClick={() => handleRemoveMember(member.id)}
          >
            <span aria-hidden="true">❌</span>
          </IconButton>
        ) : null
      }
    >
      <ListItemAvatar>
        <Avatar>{member.name[0]}</Avatar>
      </ListItemAvatar>
      <ListItemText primary={member.name} />
    </ListItem>
  ))}
</List>


        )}
      </DialogContent>

      <DialogActions>
        {members.some(m => m.id === currentUserId) && (
          <Button color="error" onClick={handleLeaveRoom}>
            Leave Room
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ListMembersDialog;
